import { Route, Switch, useHistory } from 'react-router-dom';
import { useEffect } from 'react';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddProductMutation as AddProduct } from '__generated__/graphql';
import { assetUrl } from '../../../../common/helpers';
import AddProductForm from './AddProductForm';
import GetProductQuery from './GetProductQuery';
import GetProductsQuery from './GetProductsQuery';
import ProductList from './ProductList';
import ProductSorter from './ProductSorter';
import UI from '../../../../common/UI';
import useProject from '../../../useProject';
import useQuery from '../../../../api/useQuery';
import useScroll from '../../../../common/useScroll';
import useUrlState from '../../../../common/useUrlState';

type AddedProduct = AddProduct['addProduct'];

export interface ProductsPageProps {
  eventId: string;
  productId?: string;
}

const ProductsPage = ({ eventId, productId }: ProductsPageProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const project = useProject();
  const { scrollTo } = useScroll();

  const { data, refetch } = useQuery(
    GetProductsQuery, {
      variables: {
        project_id: project.id,
        event_id: eventId,
      },
    },
  );

  const event = data?.project.event;

  const { data: productData, loading: productLoading } = useQuery(
    GetProductQuery,
    {
      variables: {
        event_id: eventId,
        product_id: productId,
      },
      skip: !productId || productId === 'sort',
      nextFetchPolicy: 'network-only',
    },
  );

  const productsSorted = () => {
    history.push(`/${project.id}/events/${event.id}/products`);
    scrollTo('EventTab');
  };

  const [urlState] = useUrlState({
    parse: (params, { string }) => ({
      product_type: string(params.product_type),
    }),
  });

  const [openAddProductModal, closeAddProductModal] = useModal(() => {
    const handleSuccess = (product: AddedProduct) => {
      closeAddProductModal();
      history.push(`/${project.id}/events/${event.id}/products/${product.id}`);
    };

    const handleClose = () => {
      closeAddProductModal();
      history.replace(`/${project.id}/events/${event.id}/products`);
    };

    return (
      <UI.Modal maxWidth={600} close={handleClose}>
        <AddProductForm
          event={event}
          standAlone={urlState.product_type === 'stand_alone_product'}
          onSuccess={handleSuccess}
        />
      </UI.Modal>
    );
  }, [project, event, urlState.product_type]);

  useEffect(() => {
    refetch();
  }, [productId, refetch]);

  useEffect(() => {
    if (event && productId === 'add') {
      openAddProductModal();
    }
  }, [event, productId, openAddProductModal]);

  if (!event) {
    return <UI.PageLoader />;
  }

  const ticketUpgrades = event.products.filter((product) => !product.stand_alone);
  const standAloneProducts = event.products.filter((product) => product.stand_alone);

  return (
    <UI.Section>
      <Switch>
        <Route path={`/${project.id}/events/${eventId}/products/sort`}>
          <ProductSorter
            event={event}
            ticketUpgrades={ticketUpgrades}
            standAloneProducts={standAloneProducts}
            onSuccess={productsSorted}
          />
        </Route>

        <Route>
          {event.products.length > 0 && (
            <UI.GridContainer>
              <UI.FlexContainer sc={{ justifyContent: 'space-between', direction: ['column', 'row'] }}>
                <UI.SectionHeading>
                  {t('products')}
                </UI.SectionHeading>

                <UI.FlexContainer sc={{ spaceX: 1 }}>
                  <UI.Button
                    onClick={openAddProductModal}
                    sc={{ brand: 'secondary' }}
                  >
                    {t('add_product')}
                  </UI.Button>
                  {(ticketUpgrades.length > 1 || standAloneProducts.length > 1) && (
                    <UI.NavButton
                      to={`/${project.id}/events/${event.id}/products/sort`}
                    >
                      {t('sort_products')}
                    </UI.NavButton>
                  )}
                </UI.FlexContainer>
              </UI.FlexContainer>

              <ProductList
                event={event}
                products={ticketUpgrades}
                productType="ticket_upgrade"
                activeProductId={productId}
                // Always pass a fresh data object
                productData={productData?.product.id === productId && !productLoading ? productData : null}
                openAddProductModal={openAddProductModal}
              />

              <ProductList
                event={event}
                products={standAloneProducts}
                productType="stand_alone_product"
                activeProductId={productId}
                // Always pass a fresh data object
                productData={productData?.product.id === productId && !productLoading ? productData : null}
                openAddProductModal={openAddProductModal}
              />
            </UI.GridContainer>
          )}

          {event.products.length === 0 && (
            <UI.Card sc={{ padding: 8, textAlign: 'center', background: 'gray.50' }}>
              <img src={assetUrl('images/ticket.svg')} alt="" height={120} />
              <UI.H3 sc={{ mb: 2 }}>{t('add_products')}</UI.H3>
              <UI.Paragraph>{t('products_get_started')}</UI.Paragraph>
              <UI.Button
                onClick={openAddProductModal}
                sc={{ brand: 'secondary' }}
              >
                {t('add_a_product')}
              </UI.Button>
            </UI.Card>
          )}
        </Route>
      </Switch>
    </UI.Section>
  );
};

export default ProductsPage;
